import './MemberJourneyLogSection.css';

import { useAtomValue } from 'jotai';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useCallback, useState } from 'react';
import {
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    IconButton,
    PencilIcon,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../common/components/GroveButtons';
import { MutationErrorToastOptions } from '../../common/components/Toasts';
import { formatToSimpleDateTimeString } from '../../common/utils/formatting';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import {
    AdminUserRole,
    isMemberJourneyMilestoneComplete,
    type MemberDetails,
    type MemberJourneyLog,
} from '../../entities/entityTypes';
import { useUpdateMemberJourneyLogAndRefetch } from '../../membership/hooks/useUpdateMemberJourneyLog';

const MEMBER_JOURNEY_LOG_FIELDS: Array<{
    key: keyof MemberJourneyLog;
    label: string;
}> = [
    { key: 'completedGatedOnboarding', label: 'Gated onboarding' },
    { key: 'viewedLinkMoreAccounts', label: 'Viewed account linking' },
    { key: 'bookedDiscoverySession', label: 'Discovery session booked' },
    { key: 'completedDiscoverySession', label: 'Discovery session done' },
    { key: 'bookedRoadmapSession', label: 'Roadmap session booked' },
    { key: 'completedRoadmapSession', label: 'Roadmaps session done' },
    { key: 'moneyMapFinalizedForAction', label: 'Money map finalized' },
    { key: 'completedMoneyMap', label: 'Money map completed' },
];

type MilestoneFieldKey = (typeof MEMBER_JOURNEY_LOG_FIELDS)[number]['key'];

interface MilestonePickerContext {
    memberId: string;
    fieldKey: MilestoneFieldKey;
    fieldLabel: string;
    memberName: string;
}

/** Human-readable completion time: ISO string → local datetime; boolean → label; else em dash. */
const milestoneCompletedAtLabel = (
    raw: MemberJourneyLog[keyof MemberJourneyLog] | undefined,
): string => {
    if (typeof raw === 'string' && raw.length > 0) {
        return formatToSimpleDateTimeString(raw);
    }
    if (raw === true) {
        return 'Completed';
    }
    return '-';
};

/** Journey milestones must not be set to a future instant. */
const clampMilestoneDateTimeToNow = (d: Date): Date => {
    const nowMs = Date.now();
    return d.getTime() > nowMs ? new Date(nowMs) : d;
};

const initialDateForPicker = (
    raw: MemberJourneyLog[keyof MemberJourneyLog] | undefined,
): Date => {
    if (typeof raw === 'string' && raw.length > 0) {
        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) {
            return clampMilestoneDateTimeToNow(parsed);
        }
    }
    return clampMilestoneDateTimeToNow(new Date());
};

const memberDisplayName = (member: MemberDetails) => {
    const name = [member.firstName, member.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    return name.length > 0 ? name : member.email;
};

const MilestonePickerActions = ({
    canClear,
    isPending,
    onCancel,
    onClear,
    onSave,
}: {
    canClear: boolean;
    isPending: boolean;
    onCancel: () => void;
    onClear: () => void;
    onSave: () => void;
}) => (
    <XStack gap={8} flexWrap="wrap" justifyContent="center" width="100%">
        {isPending ? (
            <Spinner />
        ) : (
            <>
                <Button size="small" variant="secondary" onPress={onCancel}>
                    Cancel
                </Button>
                {canClear ? (
                    <Button
                        size="small"
                        variant="secondary"
                        tone="danger"
                        onPress={onClear}
                    >
                        Clear
                    </Button>
                ) : null}
                <Button size="small" variant="primary" onPress={onSave}>
                    Save
                </Button>
            </>
        )}
    </XStack>
);

interface MemberJourneyLogSectionProps {
    members: MemberDetails[];
}

export const MemberJourneyLogSection = ({
    members,
}: MemberJourneyLogSectionProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const membership = useAtomValue(selectedMembershipAtom);
    const toast = useToastController();
    const canEditJourneyLogMilestones =
        adminUser.authorization.selectedMembership.general.canEdit &&
        (adminUser.role === AdminUserRole.SUPERADMIN ||
            adminUser.role === AdminUserRole.MEMBER_OPS);

    const { updateMemberJourneyLogAsync, isPending } =
        useUpdateMemberJourneyLogAndRefetch(membership);

    const [pickerContext, setPickerContext] =
        useState<MilestonePickerContext | null>(null);
    const [selectedDateTime, setSelectedDateTime] = useState<Date>(new Date());

    const closePicker = useCallback(() => {
        setPickerContext(null);
    }, []);

    const openPicker = useCallback(
        (
            member: MemberDetails,
            fieldKey: MilestoneFieldKey,
            fieldLabel: string,
        ) => {
            const raw = member.memberJourneyLog?.[fieldKey];
            setSelectedDateTime(initialDateForPicker(raw));
            setPickerContext({
                memberId: member.id,
                fieldKey,
                fieldLabel,
                memberName: memberDisplayName(member),
            });
        },
        [],
    );

    const handleSaveMilestone = useCallback(async () => {
        if (pickerContext == null) {
            return;
        }
        try {
            const atOrBeforeNow = clampMilestoneDateTimeToNow(selectedDateTime);
            await updateMemberJourneyLogAsync({
                userId: pickerContext.memberId,
                body: {
                    [pickerContext.fieldKey]: atOrBeforeNow.toISOString(),
                },
            });
            closePicker();
        } catch {
            toast.show(
                'Could not update journey log',
                MutationErrorToastOptions(),
            );
        }
    }, [
        pickerContext,
        selectedDateTime,
        updateMemberJourneyLogAsync,
        closePicker,
        toast,
    ]);

    const handleClearMilestone = useCallback(async () => {
        if (pickerContext == null) {
            return;
        }
        try {
            await updateMemberJourneyLogAsync({
                userId: pickerContext.memberId,
                body: {
                    [pickerContext.fieldKey]: null,
                },
            });
            closePicker();
        } catch {
            toast.show(
                'Could not clear journey milestone',
                MutationErrorToastOptions(),
            );
        }
    }, [pickerContext, updateMemberJourneyLogAsync, closePicker, toast]);

    const pickerMemberRaw = pickerContext
        ? members.find((m) => m.id === pickerContext.memberId)
              ?.memberJourneyLog?.[pickerContext.fieldKey]
        : undefined;
    const canClearPicker =
        pickerContext != null &&
        isMemberJourneyMilestoneComplete(pickerMemberRaw);

    if (members == null || members.length === 0) {
        return null;
    }

    return (
        <YStack gap={12} width="100%">
            <H6Emphasized>Member journey log</H6Emphasized>
            <BodySmallRegular marginBottom={16}>
                {`Some fields are auto-populated (and unable to be cleared) if an 'upstream' field is populated. ${members.length > 1 ? 'Membership-level fields are synced across partners when saved.' : ''}`}
            </BodySmallRegular>
            <DataTable
                value={members}
                size="small"
                tableStyle={{
                    width: '100%',
                    fontSize: 13,
                }}
                scrollable
            >
                <Column
                    field="id"
                    header="Member"
                    style={{ minWidth: 160 }}
                    body={(member: MemberDetails) => (
                        <BodySmallRegular>
                            {memberDisplayName(member)}
                        </BodySmallRegular>
                    )}
                />
                {MEMBER_JOURNEY_LOG_FIELDS.map(({ key, label }) => (
                    <Column
                        key={key}
                        field={key}
                        header={label}
                        headerStyle={{ textAlign: 'center' }}
                        body={(member: MemberDetails) => {
                            const raw = member.memberJourneyLog?.[key];
                            const completedAtLabel =
                                milestoneCompletedAtLabel(raw);
                            return (
                                <XStack
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={6}
                                    width="100%"
                                    maxWidth={200}
                                    alignSelf="center"
                                >
                                    <BodySmallRegular
                                        color="$gray1000"
                                        flexShrink={1}
                                        minWidth={0}
                                        textAlign="right"
                                        fontSize={12}
                                    >
                                        {completedAtLabel}
                                    </BodySmallRegular>
                                    <IconButton
                                        variant="secondary"
                                        size="tiny"
                                        icon={PencilIcon}
                                        disabled={
                                            !canEditJourneyLogMilestones ||
                                            isPending
                                        }
                                        onPress={() => {
                                            openPicker(member, key, label);
                                        }}
                                    />
                                </XStack>
                            );
                        }}
                        style={{
                            minWidth: 168,
                            textAlign: 'center',
                        }}
                    />
                ))}
            </DataTable>

            <Dialog
                className="member-journey-milestone-dialog"
                appendTo={
                    typeof document !== 'undefined' ? document.body : undefined
                }
                baseZIndex={5000}
                visible={pickerContext != null}
                onHide={closePicker}
                header={
                    pickerContext != null
                        ? `${pickerContext.fieldLabel} - ${pickerContext.memberName}`
                        : ''
                }
                closable
                closeOnEscape
                dismissableMask
                style={{
                    width: 'min(98vw, 36rem)',
                    maxWidth: '36rem',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                }}
                contentStyle={{
                    flex: '0 0 auto',
                    overflow: 'visible',
                }}
                draggable={false}
                resizable={false}
                footer={
                    <MilestonePickerActions
                        canClear={canClearPicker}
                        isPending={isPending}
                        onCancel={closePicker}
                        onClear={() => {
                            void handleClearMilestone();
                        }}
                        onSave={() => {
                            void handleSaveMilestone();
                        }}
                    />
                }
            >
                {pickerContext != null ? (
                    <YStack
                        gap={4}
                        padding={2}
                        overflow="visible"
                        width="100%"
                        minWidth={0}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <BodySmallEmphasized fontSize={12}>
                            Completion date and time
                        </BodySmallEmphasized>
                        {/*
                          `inline` renders the calendar in the dialog body. The default
                          input + popup mode opens a separate overlay that sat above our
                          footer and hid Save/Cancel.
                        */}
                        <div
                            style={{
                                minWidth: 0,
                                overflow: 'visible',
                                marginTop: 16,
                                marginBottom: 32,
                            }}
                        >
                            <Calendar
                                inline
                                disabled={isPending}
                                maxDate={new Date()}
                                value={selectedDateTime}
                                onChange={(e) => {
                                    const next = e.value as
                                        | Date
                                        | null
                                        | undefined;
                                    const resolved =
                                        next != null &&
                                        !Number.isNaN(next.getTime())
                                            ? next
                                            : new Date();
                                    setSelectedDateTime(
                                        clampMilestoneDateTimeToNow(resolved),
                                    );
                                }}
                                showTime
                                hourFormat="12"
                                showButtonBar
                                dateFormat="mm/dd/yy"
                            />
                        </div>
                    </YStack>
                ) : null}
            </Dialog>
        </YStack>
    );
};
